import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from "../services/api";

const LOGO_CACHE_KEY = "website_logo";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  // State variables for dynamic data
  const [logo, setLogo] = useState<string | null>(null);
  const [brands, setBrands] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  // Fetch dynamic data on mount
  useEffect(() => {
    // 1. Fetch Logo
    const cachedLogo = localStorage.getItem(LOGO_CACHE_KEY);
    if (cachedLogo) setLogo(cachedLogo);

    api.get("/website/logo").then((res: any) => {
      if (res?.logo) { 
        setLogo(res.logo); 
        localStorage.setItem(LOGO_CACHE_KEY, res.logo); 
      }
    }).catch(() => {});

    // 2. Fetch Brands
    api.get("/brands/popular").then((res: any) => {
      setBrands(res || []);
    }).catch(() => {});

    // 3. Fetch Cities
    api.get("/cities").then((res: any) => {
      const cityList = Array.isArray(res) ? res : (res.data || []);
      setCities(cityList);
    }).catch(() => {});
  }, []);

  return (
    <footer className="bg-[#05050A] text-slate-400 pt-24 pb-8 overflow-hidden relative border-t border-slate-900">
      
      {/* Ambient Premium Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[#363C90]/50 to-transparent" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#363C90]/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#21C2A5]/5 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="max-w-[1300px] mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-8">
          
          {/* 1. Dynamic Brand & Philosophy */}
          <div className="md:col-span-4 space-y-6">
            <div className="cursor-pointer" onClick={() => navigate('/')}>
              {logo ? (
                <img 
                  src={logo} 
                  alt="Sell30" 
                  className="h-10 w-auto object-contain" 
                  // If your logo is dark, you can add 'brightness-0 invert' in className to make it white in the dark footer
                />
              ) : (
                <div className="flex items-center tracking-tighter font-black text-3xl text-white">
                  <span>Sell</span><span className="text-[#21C2A5]">30</span>
                </div>
              )}
            </div>
            <p className="text-[14px] leading-relaxed max-w-sm font-medium text-slate-500">
              Premium circular economy platform. We prolong the life of technology 
              through transparency, trust, and guaranteed best prices.
            </p>
          </div>

          {/* 2. Dynamic Brands Column */}
          <div className="md:col-span-2 md:col-start-6">
            <h4 className="text-[12px] font-bold text-white mb-6 uppercase tracking-[0.2em]">Top Brands</h4>
            <ul className="space-y-4">
              {brands.length > 0 ? brands.slice(0, 5).map((brand) => (
                <li 
                  key={brand.id} 
                  onClick={() => navigate(`/sell/products/${brand.id}?brand=${brand.name}`)}
                  className="group flex items-center gap-2 text-[13px] hover:text-white cursor-pointer transition-all"
                >
                  <span className="w-0 h-px bg-[#21C2A5] group-hover:w-4 transition-all duration-300" />
                  Sell {brand.name}
                </li>
              )) : (
                // Fallback loading skeleton
                [...Array(4)].map((_, i) => (
                  <li key={i} className="h-4 w-24 bg-slate-800 rounded animate-pulse" />
                ))
              )}
            </ul>
          </div>

          {/* 3. Dynamic Cities Column */}
          <div className="md:col-span-2">
            <h4 className="text-[12px] font-bold text-white mb-6 uppercase tracking-[0.2em]">Top Cities</h4>
            <ul className="space-y-4">
              {cities.length > 0 ? cities.slice(0, 5).map((city) => (
                <li 
                  key={city.id} 
                  className="text-[13px] hover:text-[#21C2A5] cursor-pointer transition-colors"
                >
                  Sell in {city.name}
                </li>
              )) : (
                // Fallback loading skeleton
                [...Array(4)].map((_, i) => (
                  <li key={i} className="h-4 w-24 bg-slate-800 rounded animate-pulse" />
                ))
              )}
            </ul>
          </div>

          {/* 4. Support Card */}
          <div className="md:col-span-3 flex flex-col md:items-end">
            <h4 className="text-[12px] font-bold text-white mb-6 uppercase tracking-[0.2em]">Support</h4>
            <a 
              href="mailto:support@sell30.com" 
              className="group relative flex items-center gap-4 bg-[#0A0C14] border border-slate-800/60 p-4 rounded-2xl hover:border-[#363C90]/50 transition-all duration-500 overflow-hidden w-full max-w-[260px]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#363C90]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10 p-3 bg-gradient-to-br from-[#1E1B4B] to-[#363C90] rounded-xl text-white group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Mail size={18} />
              </div>
              <div className="relative z-10 text-left">
                <p className="text-[10px] font-bold text-[#21C2A5] uppercase tracking-wider mb-0.5">Response in 24h</p>
                <p className="text-[14px] font-semibold text-white tracking-tight group-hover:text-blue-100 transition-colors">support@sell30.com</p>
              </div>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-20 pt-8 border-t border-slate-800/60 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#21C2A5] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#21C2A5]"></span>
            </div>
            <p className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
              Systems Operational
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6 text-[12px] text-slate-500 font-medium">
            <p>© {currentYear} Sell30. All rights reserved.</p>
            <div className="hidden md:block w-1 h-1 rounded-full bg-slate-700" />
            <p>Web and App Development by Codebazar</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;